import { Component, Inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogContent, MatDialogActions, MatDialogTitle } from '@angular/material/dialog'
import { MatButton } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatChipsModule } from '@angular/material/chips'
import { MatIconModule } from '@angular/material/icon'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { CommonModule } from '@angular/common'
import { TranslateModule } from '@ngx-translate/core'
import { TemplateItem } from '../types/template.types'

export type TemplateItemCreateEditModalInputData = {
  item: TemplateItem
  isCreating: boolean
  attendeeNames: string[]
}

export type TemplateItemCreateEditModalOutputData = {
  title: string
  duration: number | null
  presenters: string[]
}

@Component({
  selector: 'app-template-item-edit-modal',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogContent,
    MatDialogActions,
    MatDialogTitle,
    MatButton,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    TranslateModule,
  ],
  templateUrl: './template-item-create-edit-modal.html',
  styleUrl: './template-item-create-edit-modal.scss',
})
export class TemplateItemCreateEditModal {
  title: string
  duration: number | null
  presenters: string[]
  newPresenterName = ''
  filteredAttendeeNames: string[] = []

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TemplateItemCreateEditModalInputData,
    private dialogRef: MatDialogRef<TemplateItemCreateEditModal>
  ) {
    this.title = data.item.title
    this.duration = data.item.duration
    this.presenters = [...data.item.presenters]
    this.filteredAttendeeNames = [...data.attendeeNames]
  }

  filterAttendeeNames(): void {
    const filterValue = this.newPresenterName.toLowerCase()
    this.filteredAttendeeNames = this.data.attendeeNames
      .filter((name) => !this.presenters.includes(name))
      .filter((name) => name.toLowerCase().includes(filterValue))
  }

  get dialogTitleKey(): string {
    return this.data.isCreating ? 'TEMPLATES.EDITOR.CREATE_ITEM_DIALOG.TITLE' : 'TEMPLATES.EDITOR.EDIT_ITEM_DIALOG.TITLE'
  }

  get dialogDescriptionKey(): string {
    return this.data.isCreating ? 'TEMPLATES.EDITOR.CREATE_ITEM_DIALOG.DESCRIPTION' : 'TEMPLATES.EDITOR.EDIT_ITEM_DIALOG.DESCRIPTION'
  }

  addPresenter(): void {
    const trimmed = this.newPresenterName.trim()
    if (trimmed === '') {
      this.onSave()
    }
    if (trimmed !== '' && !this.presenters.includes(trimmed)) {
      this.presenters = [...this.presenters, trimmed]
      this.newPresenterName = ''
    }
  }

  removePresenter(presenterToRemove: string): void {
    this.presenters = this.presenters.filter((presenter) => presenter !== presenterToRemove)
  }

  onSave(): void {
    if (this.newPresenterName.trim() !== '') {
      this.addPresenter()
    }
    const result: TemplateItemCreateEditModalOutputData = {
      title: this.title,
      duration: this.duration,
      presenters: this.presenters,
    }
    this.dialogRef.close(result)
  }

  onCancel(): void {
    this.dialogRef.close(null)
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !this.isSaveDisabled) {
      event.preventDefault()
      this.onSave()
    }
  }

  get isSaveDisabled(): boolean {
    return this.title.trim() === ''
  }
}
